import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SiteLayout from "@/components/SiteLayout";
import { Button } from "@/components/ui/button";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  CITY_DATA,
  IMAGES,
  buildServicePageContent,
  getCityServicePath,
} from "@/data/siteContent";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
  viewport: { once: true },
};

const ServicePage = ({ language = "fr", serviceKey }) => {
  const content = buildServicePageContent(language, serviceKey);
  const metaTitle = content.metaTitle || "Pavé uni Rive-Sud";
  const metaDescription =
    content.metaDescription ||
    "Service résidentiel de pavé uni sur la Rive-Sud.";

  usePageMeta(metaTitle, metaDescription);
  const isFr = language === "fr";
  const heroImageBase = content.heroImage || IMAGES.heroInstallation;
  const heroImageSrcSet = `${heroImageBase}&w=640&q=60 640w, ${heroImageBase}&w=960&q=65 960w, ${heroImageBase}&w=1400&q=70 1400w`;
  const galleryImages = [IMAGES.heroInstallation, IMAGES.workerAction, IMAGES.textureOldStone];

  return (
    <SiteLayout language={language}>
      <section className="bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 grid gap-12 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <motion.div {...fadeUp} className="space-y-6">
            <p className="text-xs uppercase tracking-[0.4em] text-secondary" data-testid="service-hero-eyebrow">
              {content.hero.eyebrow}
            </p>
            <h1 className="text-3xl md:text-5xl font-bold text-primary" data-testid="service-page-h1">
              {content.h1}
            </h1>
            <p className="text-lg text-muted-foreground" data-testid="service-hero-subtitle">
              {content.hero.subtitle}
            </p>
            <Button
              asChild
              className="rounded-none uppercase tracking-wider font-bold"
              data-testid="service-hero-cta"
            >
              <Link to={isFr ? "/fr/contact" : "/en/contact"}>{content.hero.cta}</Link>
            </Button>
          </motion.div>
          <motion.div {...fadeUp}>
            <img
              src={`${heroImageBase}&w=1400&q=70`}
              srcSet={heroImageSrcSet}
              sizes="(max-width: 1024px) 90vw, 520px"
              alt={content.h1}
              className="w-full h-[420px] object-cover shadow-2xl"
              loading="eager"
              fetchPriority="high"
              data-testid="service-hero-image"
            />
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 space-y-10">
          <motion.p {...fadeUp} className="text-lg text-muted-foreground" data-testid="service-intro">
            {content.intro}
          </motion.p>
          <motion.div {...fadeUp} className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" data-testid="service-problem-title">
              {content.problemTitle}
            </h2>
            <p className="text-muted-foreground" data-testid="service-problem-text">
              {content.problemText}
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" data-testid="service-solution-title">
              {content.solutionTitle}
            </h2>
            <p className="text-muted-foreground" data-testid="service-solution-text">
              {content.solutionText}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl font-bold" data-testid="service-process-title">
              {content.processTitle}
            </h2>
            <div className="grid gap-6 mt-10 md:grid-cols-3">
              {content.processSteps.map((step, index) => (
                <div key={step.title} className="border border-slate-700 p-6" data-testid={`service-process-step-${index + 1}`}>
                  <p className="text-secondary text-lg font-bold">{`0${index + 1}`}</p>
                  <h3 className="mt-3 font-semibold">{step.title}</h3>
                  <p className="text-sm text-slate-200 mt-2">{step.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl font-bold text-primary" data-testid="service-gallery-title">
              {content.galleryTitle}
            </h2>
            <div className="grid gap-6 mt-10 md:grid-cols-3">
              {galleryImages.map((image, index) => (
                <img
                  key={image}
                  src={`${image}&w=800&q=65`}
                  srcSet={`${image}&w=480&q=60 480w, ${image}&w=800&q=65 800w, ${image}&w=1200&q=70 1200w`}
                  sizes="(max-width: 768px) 90vw, 280px"
                  alt="Pavé uni"
                  className="h-64 w-full object-cover"
                  loading="lazy"
                  data-testid={`service-gallery-image-${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl font-bold text-primary" data-testid="service-cities-title">
              {isFr ? "Pages locales" : "Local pages"}
            </h2>
            <p className="text-muted-foreground mt-2" data-testid="service-cities-subtitle">
              {isFr
                ? "Choisissez votre ville pour une approche locale et des détails spécifiques."
                : "Choose your city for a localized approach and specific details."}
            </p>
            <div className="grid gap-6 mt-8 md:grid-cols-2 lg:grid-cols-3">
              {CITY_DATA.map((city) => (
                <Link
                  key={city.slug}
                  to={getCityServicePath(language, city.slug, serviceKey)}
                  className="border border-border bg-white p-5 shadow-sm hover:shadow-md transition"
                  data-testid={`service-city-${city.slug}-link`}
                >
                  <p className="text-sm uppercase tracking-[0.3em] text-secondary">
                    {isFr ? "Ville" : "City"}
                  </p>
                  <h3 className="text-xl font-bold text-primary mt-2">
                    {isFr ? city.nameFr : city.nameEn}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    {isFr ? city.landmarkFr : city.landmarkEn}
                  </p>
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-3xl font-bold text-primary" data-testid="service-final-cta-title">
              {content.ctaTitle}
            </h2>
            <p className="text-muted-foreground" data-testid="service-final-cta-text">
              {content.ctaText}
            </p>
            <Button
              asChild
              className="rounded-none uppercase tracking-wider font-bold"
              data-testid="service-final-cta-button"
            >
              <Link to={isFr ? "/fr/contact" : "/en/contact"}>{content.ctaButton}</Link>
            </Button>
            <div className="pt-4">
              <Link
                to={content.relatedService.path}
                className="text-sm uppercase tracking-widest text-secondary"
                data-testid="service-related-link"
              >
                {isFr
                  ? `Voir aussi : ${content.relatedService.label}`
                  : `Also explore: ${content.relatedService.label}`}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default ServicePage;